import { memo, useEffect, useCallback } from "preact/compat"
import UserInfo from "@/features_signal/userInfo"
import { useSignal, computed } from "@preact/signals"
import { fetchBlindBoxDetail, blindBoxDetail, fetchBlindBoxClaim } from "@/features_signal/blindbox"
import dayjs from "dayjs"
import { useLocation } from "react-router-dom"
import Modal from "./Modal"
import { Frame, FrameSvg } from "./styled"
import { createLogger } from "@/utils/logger"

const logger = createLogger("BlindBoxModal")

// 不显示盲盒的路由
const hideFramePaths = ["/s-game-page", "/s-points", "/invite", "/s-mall"]

/**
 * BlindBoxModal 组件：用于显示和管理盲盒模态框的逻辑。
 * 包括盲盒数据的获取、过滤、领取以及模态框的显示控制。
 */
const BlindBoxModal: React.FC = () => {
    const local = useLocation() // 获取当前路由信息
    const full = useSignal(true) // 控制模态框是否全屏显示
    const show = useSignal(false) // 控制模态框是否显示
    const ym = useSignal<{ year: string; month: string }>({
        year: String(dayjs().year()), // 当前年份
        month: String(dayjs().month() + 1), // 当前月份
    }) // 当前年月状态

    // 计算属性：将年月格式化为 "YYYY-MM" 字符串
    const ymStr = computed(() => {
        return `${ym.value.year}-${ym.value.month.padStart(2, "0")}`
    })

    // 计算属性：过滤未过期的盲盒数据
    const dataSource = computed(() => {
        return (
            blindBoxDetail.value?.data?.[ymStr.value]?.filter(
                (item) =>
                    !item.is_expired &&
                    !item.state &&
                    !!item.expire_second &&
                    item.expire_second > 5, // 有效期剩余5秒的盲盒不显示
            ) ?? []
        )
    })

    // 当前显示的盲盒数据
    const currentData = useSignal<BlindBoxApiTypes.Fetcher.Response.Record[]>([])

    // 显示盲盒活动路由
    const hasPath = computed(() => {
        return !hideFramePaths.some((path) => location.pathname.startsWith(path))
    })

    // 显示悬浮盒子
    const showFrame = computed(() => {
        return UserInfo.isLogin.value && currentData.value.length > 0 && hasPath.value
    })

    // 显示盲盒模态框
    const showModal = computed(() => {
        return UserInfo.isLogin.value && show.value && hasPath.value
    })

    /**
     * 更新当前显示的盲盒数据
     * @param data 新的盲盒数据
     */
    const updateData = useCallback(
        (data: BlindBoxApiTypes.Fetcher.Response.Record[]) => {
            currentData.value = data
        },
        [currentData],
    )

    // 当数据源变化时更新当前数据
    useEffect(() => {
        updateData(dataSource.value)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSource.value.length, updateData])

    // 当用户登录状态或路由变化时，重新获取盲盒数据
    useEffect(() => {
        if (local.pathname !== "/blind-box" && UserInfo.isLogin.value) {
            fetchBlindBox()
            if (dataSource.value.length > 0) updateData(dataSource.value)
        }
        if (!UserInfo.isLogin.value) {
            full.value = true
            show.value = false
            updateData([])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [UserInfo.isLogin.value])

    // 当当前数据变化时，控制模态框的显示状态
    useEffect(() => {
        if (currentData.value.length > 0) show.value = true
        else show.value = false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentData.value.length])

    /**
     * 获取盲盒数据的异步函数
     */
    const fetchBlindBox = useCallback(async () => {
        await fetchBlindBoxDetail({
            pid: "1",
            ym: ymStr.value,
        })
    }, [ymStr.value])

    /**
     * 处理盲盒领取逻辑
     * @param boxId 盲盒ID
     */
    const handleClaim = useCallback(async (boxId: string) => {
        await fetchBlindBoxClaim({
            box_id: boxId,
            pid: "1",
        })
    }, [])

    /**
     * 动画结束后重新获取数据
     */
    const onClaimAll = useCallback(() => {
        show.value = false
        setTimeout(() => {
            fetchBlindBox()
        }, 1000)
    }, [fetchBlindBox, show])

    return (
        <>
            {/* 登录且显示条件满足时渲染模态框 */}
            {showModal.value ? (
                <Modal
                    full={full.value}
                    dataSource={currentData.value}
                    onCancel={() => {
                        full.value = false
                    }}
                    handleClaim={async (boxId) => {
                        await handleClaim(boxId)
                    }}
                    onClaimAll={onClaimAll}
                    onTimeEnd={onClaimAll}
                />
            ) : null}

            {/* 登录且有数据时渲染触发按钮 */}
            {showFrame.value ? (
                <Frame
                    onClick={() => {
                        full.value = true
                        if (!show.value) show.value = true
                    }}
                >
                    <FrameSvg />
                </Frame>
            ) : null}
        </>
    )
}

export default memo(BlindBoxModal)
